import { useCallback, useEffect, useMemo, useState } from 'react';
import { cn, isEmpty } from '@/lib/utils.ts';
import { Button } from '@/components/ui/button.tsx';
import { BrushIcon, ChevronLeftIcon, ChevronRightIcon, SquareDashedIcon } from 'lucide-react';
import { Spinner } from '@/components/app/spinner.tsx';
import { getImgOrNoPreview, TListColor, TListProduct, TUser } from '@/types/models.ts';
import { AdminDropdown } from '@/app/products/list/admin/admin-dropdown.tsx';
import { setAddToProjectItem } from '@/stores/ui.store.ts';
import { AppTooltip } from '@/components/app/app-tooltip.tsx';
import { Link } from '@tanstack/react-router';
import { routes } from '@/router/routes-urls.ts';
import { AppHeartIcon } from '@/app/products/app-heart-icon.tsx';
import { useVisibleImageLoader } from '@/hooks/use-visible-image-loader.ts';

type Props = {
  item: TListProduct;
  selectedColor?: number;
  user: TUser;
};

function getPrice(cost: number) {
  if (!cost) {
    return null;
  }
  let str = '';
  for (let i = 0; i < cost; i++) {
    str += '$';
  }
  return <div className="text-lg leading-none text-gray-400">{str}</div>;
}

function getSize(item: TListProduct) {
  if (!item.size_str) {
    return null;
  }
  return <div className="text-lg leading-none text-gray-400">{item.size_str}</div>;
}

function getSelectedIndex(item: TListProduct, selectedColor?: number): number {
  let _index = 0;
  if (item.colors.length > 0) {
    item.colors.forEach((color, _i) => {
      color.c_p.forEach((ci) => {
        if (ci.id === selectedColor) {
          _index = _i;
        }
      });
    });
  }
  return _index;
}

function isBroadloom(item: TListProduct): boolean {
  return item.type === 1;
}

function isCustom(item: TListProduct): boolean {
  return item.type === 0;
}

function getAddToProjectTooltip(item: TListProduct): string {
  if (!item?.in_projects) {
    return 'Add to Project';
  }
  if (item.in_projects) {
    return `In ${item.in_projects} Projects`;
  } else if (item.in_projects) {
    return `In 1 Project`;
  }
  return 'Add to Project';
}

function getColorName(colorName?: string, productName?: string) {
  if (!colorName || !productName) {
    return null;
  }
  if (colorName.toLowerCase() === productName.toLowerCase()) {
    return null;
  }
  return <h4 className="leading-none">- {colorName}</h4>;
}

export function ProductItem({ item, selectedColor, user }: Props) {
  const { elementRef, isVisible, imageLoaded, setImageLoaded, loadImage } = useVisibleImageLoader();
  const _currIndex: number = useMemo(() => getSelectedIndex(item, selectedColor), [item, selectedColor]);
  const [currIndex, setCurrIndex] = useState(_currIndex);
  const color = item.colors[currIndex] as TListColor | undefined;

  const loadColorImage = useCallback(() => {
    if (!isVisible) {
      return;
    }
    const _color = item.colors[currIndex] as TListColor | undefined;
    if (_color) {
      loadImage(getImgOrNoPreview(_color.img));
    }
  }, [currIndex, isVisible, item.colors, loadImage]);

  useEffect(() => {
    loadColorImage();
  }, [currIndex, item, isVisible]);

  useEffect(() => {
    let _index = getSelectedIndex(item, selectedColor);
    if (_index !== currIndex) {
      setCurrIndex(_index);
      loadColorImage();
    }
  }, [selectedColor]);

  const goToPrevious = () => {
    if (item.colors.length < 2) {
      return;
    }
    setImageLoaded(false);
    setCurrIndex((prevIndex) => (prevIndex === 0 ? item.colors.length - 1 : prevIndex - 1));
  };
  const goToNext = () => {
    if (item.colors.length < 2) {
      return;
    }
    setImageLoaded(false);
    setCurrIndex((prevIndex) => (prevIndex === item.colors.length - 1 ? 0 : prevIndex + 1));
  };

  const onAddToProject = () => {
    setAddToProjectItem({
      productId: item.id,
      colorId: color ? color.id : 0,
      name: item.name + (color ? ` - ${color.name}` : ''),
    });
  };

  return (
    <div className="product" ref={elementRef} data-curr-index={currIndex}>
      <div className={cn('image-box', item.colors.length < 2 && 'no-nav', !imageLoaded && 'loading')}>
        <div
          className={cn(
            'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10',
            imageLoaded ? 'hidden' : 'block',
          )}
        >
          <div className="flex items-center gap-6">
            <Spinner className="size-12 opacity-20" />
          </div>
        </div>
        {user.admin && <AdminDropdown item={item} user={user} currIndex={currIndex} />}
        {isBroadloom(item) && (
          <div className="type-icon">
            <AppTooltip tooltip="Broadloom">
              <SquareDashedIcon className="size-4" />
            </AppTooltip>
          </div>
        )}
        {isCustom(item) && (
          <div className="type-icon">
            <AppTooltip tooltip="Custom">
              <BrushIcon className="size-4" />
            </AppTooltip>
          </div>
        )}
        <Link to={routes.productView(item.id, color?.id)} preload={false} viewTransition={true}>
          <div
            className={cn('cur', imageLoaded && 'fade-in')}
            style={{
              backgroundImage: isVisible ? `url(${getImgOrNoPreview(color?.img)})` : 'none',
            }}
          ></div>
        </Link>
        <Button variant="ghost" size="icon" onClick={goToPrevious} className="nav-button prev w-5 h-10">
          <ChevronLeftIcon className="size-8" />
        </Button>
        <Button variant="ghost" size="icon" onClick={goToNext} className="nav-button next w-5 h-10">
          <ChevronRightIcon className="size-8" />
        </Button>
        <div className="nav-dots">
          {item.colors.map((_, index) => (
            <span
              key={index}
              className={cn('dot', index === currIndex && 'active')}
              title={item.colors[index]?.name}
              onClick={() => {
                if (index !== currIndex) {
                  setImageLoaded(false);
                  setCurrIndex(index);
                }
              }}
            ></span>
          ))}
        </div>
      </div>
      <div className="flex justify-between px-2 py-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-xl font-medium leading-none">{item.name}</h3>
            {getColorName(color?.name, item.name)}
          </div>
          {item.price && getPrice(item.price)}
          {item.type === 2 && getSize(item)}
        </div>
        <div>
          <AppTooltip tooltip={getAddToProjectTooltip(item)}>
            <Button variant="ghost" size="icon-sm" onClick={onAddToProject}>
              <AppHeartIcon filled={!isEmpty(item.in_projects)} />
            </Button>
          </AppTooltip>
        </div>
      </div>
    </div>
  );
}
